/**
 * The "Other ways to add" of the New Connection dialog (Option C, C1): the same four entry points on the start view
 * (listbox options), the rail (buttons pinned at its bottom) and the compact bar's "Add from…" menu. Their ids and
 * `data-testid`s are the selector contract (plan appendix); what each one does lives in `useEntryPoints`.
 */
import { ClipboardPaste, Cloud, Download, Radar, type LucideIcon } from 'lucide-react';
import { IMPORT_SOURCES } from '../../connection-import/connectionImportSources';

export type EntryPointId = 'paste' | 'browse_azure' | 'detect' | 'import';

/** Where an entry point was used (`feature:connection_entry_point.view`, `placement` on the import / detect events). */
export type EntryPlacement = 'start' | 'rail' | 'compact';

export type EntryPointInfo = {
  readonly id: EntryPointId;
  readonly label: string;
  /** The start view's second line. */
  readonly description: string;
  readonly testId: string;
  readonly Icon: LucideIcon;
};

const NAMED_IMPORT_TOOLS = 3;

export const ENTRY_POINTS: readonly EntryPointInfo[] = [
  { id: 'paste', label: 'Paste connection string', description: 'ADO.NET, URL, JDBC or EZConnect', testId: 'paste-connection-string-button', Icon: ClipboardPaste },
  {
    id: 'browse_azure',
    label: 'Browse Azure',
    description: 'Pick SQL, PostgreSQL, Data Explorer or Storage resources',
    testId: 'browse-azure-new-connection-button',
    Icon: Cloud,
  },
  { id: 'detect', label: 'Detect local databases', description: 'Docker containers and local services', testId: 'detect-docker-new-connection-button', Icon: Radar },
  {
    id: 'import',
    label: 'Import from another tool',
    description: `SSMS, Azure Data Studio, DBeaver and ${IMPORT_SOURCES.length - NAMED_IMPORT_TOOLS} more`,
    testId: 'import-connections-new-connection-button',
    Icon: Download,
  },
];

export function entryPoint(id: EntryPointId): EntryPointInfo {
  const entry = ENTRY_POINTS.find((candidate) => candidate.id === id);
  if (!entry) throw new Error(`Unknown entry point "${id}"`);
  return entry;
}
